import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CoreSessionStorage } from '../../storage/session/CoreSessionStorage';
import { useCoreDispatch } from '../../store/CoreStore';
import { usePopupSlice } from '../../store/slices/popup/selectors';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { isCurrentBrowserIOS } from '../../utils/browsers';

export const VolumeRestorer: FC = (): null => {
  const isEventAdded = useRef(false);
  const channels = useSettingsSlice.getVolumeChannels();
  const masterVolume = useSettingsSlice.getMasterChannel().value;
  const previousMasterVolume = useSettingsSlice.getMasterChannel().previousValue;
  const currentPopup = usePopupSlice.currentPopup();
  const dispatch = useCoreDispatch();
  const wasSomeChannelEnabled = useSettingsSlice.getWasAnySoundChannelEnabled();
  const isStudioSoundDisabled = useSettingsSlice.getIsStudioSoundDisabled();

  const previousVolumes = useMemo(() => {
    const volumes: Record<string, number | null> = {};

    Object.keys(channels).forEach((channelName) => {
      volumes[channelName] = channels[channelName]?.previousValue ?? null;
    });

    return volumes;
  }, [channels]);

  const restoreVolume = useCallback(
    (event: Event): void => {
      // setting flag to true as this was one-time event
      isEventAdded.current = true;

      const eventName = event.type;

      // Toggle master volume for non-iOS devices
      if (!isCurrentBrowserIOS && !masterVolume) {
        dispatch(settingsActions.setMasterVolumeOnly(previousMasterVolume));
        document.removeEventListener(eventName, restoreVolume);

        return;
      }

      if (isCurrentBrowserIOS) {
        // Toggle channel volume for iOS devices
        Object.keys(channels).forEach((channel: string) => {
          if (isStudioSoundDisabled && channel === 'studio') return;

          const wasChannelEnabled = !!previousVolumes[channel];

          if (wasChannelEnabled) {
            dispatch(settingsActions.setVolumeIOSToggle({ channel: channel, value: 1 }));
            dispatch(settingsActions.setMasterVolumeOnly(1));
          }
        });
      }

      document.removeEventListener(eventName, restoreVolume);
    },
    [channels, dispatch, masterVolume, previousMasterVolume, previousVolumes, isStudioSoundDisabled]
  );

  useEffect(() => {
    const lastMasterVolume = CoreSessionStorage.getLastMasterVolume();
    const wasMasterEnabled = lastMasterVolume === null ? true : !!lastMasterVolume;
    const shouldRestoreVolume =
      wasMasterEnabled &&
      wasSomeChannelEnabled &&
      !isEventAdded.current &&
      !currentPopup &&
      isStudioSoundDisabled !== undefined;

    if (shouldRestoreVolume) {
      document.addEventListener('click', restoreVolume, { once: true });

      return (): void => document.removeEventListener('click', restoreVolume);
    }
  }, [previousVolumes, restoreVolume, currentPopup, wasSomeChannelEnabled, isStudioSoundDisabled]);

  return null;
};
